<template>
  <div class="step-resources">
    <div class="toolbar">
      <button type="button" class="btn-action" :disabled="!canManage" @click="openPicker">
        {{ $t('bo.programs.creation.addResource') }}
      </button>
    </div>

    <div v-if="store.isLoadingResources" class="state">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="!store.resources.length" class="state empty">{{ $t('bo.programs.creation.noResources') }}</div>

    <ul v-else class="resource-list">
      <li v-for="resource in store.resources" :key="resource.resource_id ?? resource.id" class="resource-item">
        <div>
          <div class="resource-title">{{ resource.title }}</div>
          <div class="resource-meta">{{ resource.type }}</div>
        </div>
        <button
          type="button"
          class="link-btn danger"
          :disabled="!canManage"
          @click="remove(resource.resource_id ?? resource.id)"
        >
          {{ $t('bo.common.remove') }}
        </button>
      </li>
    </ul>

    <div v-if="showPicker" class="modal-overlay" @click.self="showPicker = false">
      <div class="modal">
        <h3>{{ $t('bo.programs.creation.pickResources') }}</h3>
        <input v-model="search" type="text" class="input" :placeholder="$t('bo.common.search')" />
        <div v-if="loadingAvailable" class="state">{{ $t('bo.common.loading') }}</div>
        <ul v-else class="picker-list">
          <li v-for="item in filteredAvailable" :key="item.id">
            <label class="picker-row">
              <input v-model="selectedIds" type="checkbox" :value="item.id" />
              <span>{{ item.title }}</span>
            </label>
          </li>
        </ul>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="showPicker = false">{{ $t('bo.common.cancel') }}</button>
          <button type="button" class="btn-primary" :disabled="!selectedIds.length || store.isSubmitting" @click="addSelected">
            {{ $t('bo.common.add') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useAdminProgramCreationStore } from '@/stores/adminProgramCreation'
import adminProgramService from '@/services/adminProgramService'

const store = useAdminProgramCreationStore()

const showPicker = ref(false)
const loadingAvailable = ref(false)
const available = ref([])
const selectedIds = ref([])
const search = ref('')

const canManage = computed(() => store.programId != null && !store.isArchivedProgram)

const addedIds = computed(() =>
  store.resources.map((r) => r.resource_id ?? r.id).filter(Boolean)
)

const filteredAvailable = computed(() => {
  const q = search.value.trim().toLowerCase()
  return available.value.filter((item) => {
    if (addedIds.value.includes(item.id)) return false
    if (!q) return true
    return (item.title || '').toLowerCase().includes(q)
  })
})

const openPicker = async () => {
  showPicker.value = true
  selectedIds.value = []
  loadingAvailable.value = true
  try {
    const res = await adminProgramService.getAvailableResources()
    const data = res?.data?.data ?? res?.data ?? []
    available.value = Array.isArray(data) ? data : []
  } finally {
    loadingAvailable.value = false
  }
}

const addSelected = async () => {
  await store.addResources(selectedIds.value)
  showPicker.value = false
}

const remove = async (resourceId) => {
  await store.removeResource(resourceId)
}

onMounted(async () => {
  if (store.programId) await store.fetchResources()
})

watch(
  () => store.programId,
  async (id) => {
    if (id) await store.fetchResources()
  }
)
</script>

<style scoped>
.toolbar {
  margin-bottom: 1rem;
}

.btn-action {
  padding: 0.45rem 0.9rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.resource-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.resource-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.resource-title {
  font-weight: 600;
}

.resource-meta {
  font-size: 0.8rem;
  color: #6b7280;
}

.link-btn {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
}

.link-btn.danger {
  color: #dc2626;
}

.state {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  padding: 1.25rem;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal h3 {
  margin: 0 0 1rem;
}

.input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
  box-sizing: border-box;
}

.picker-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
}

.picker-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.45rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-secondary {
  background: white;
  border: 1px solid #d1d5db;
}
</style>
